import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import {
  IonContent,
  IonHeader,
  IonSegment,
  IonSegmentButton,
  IonSkeletonText,
  IonTitle,
  IonToolbar,
  ModalController,
} from '@ionic/angular/standalone';
import { PaginationBoxComponent } from '@qid/super';
import { PaginatedResponse } from '../../../../interface/PaginatedResponse';
import { PlanPurchaseStatusFilter } from '../../dto/get-plan-purchase-query.dto';
import { PlanPurchase } from '../../models/plan-purchase.model';
import { PlanPurchaseMasterService } from '../../services/plan-purchase-master.service';
import { ViewPlanPurchaseDetailPage } from '../view-plan-purchase-detail/view-plan-purchase-detail.page';

type StatusSegment = 'all' | PlanPurchaseStatusFilter;
type PurchaseStatus = 'Active' | 'Expired' | 'Deactivated';

@Component({
  selector: 'app-list-plan-purchase',
  templateUrl: './list-plan-purchase.page.html',
  styleUrls: ['./list-plan-purchase.page.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    IonHeader,
    IonToolbar,
    IonTitle,
    IonContent,
    IonSegment,
    IonSegmentButton,
    IonSkeletonText,
    PaginationBoxComponent,
  ],
})
export class ListPlanPurchasePage implements OnInit {
  public paginatedPurchases: PaginatedResponse<PlanPurchase> = { docs: [] };
  public isLoading = false;
  public currentPage = 1;
  public statusSegment: StatusSegment = 'all';
  readonly gridClass =
    'grid w-full grid-cols-[minmax(0,1.6fr)_minmax(0,1.2fr)_minmax(0,1.1fr)_minmax(0,1.1fr)_minmax(0,0.8fr)_minmax(0,0.9fr)_minmax(0,0.9fr)] items-center gap-4';
  readonly skeletonCells = [1, 2, 3, 4, 5, 6, 7];

  constructor(
    private planPurchaseMasterService: PlanPurchaseMasterService,
    private modalController: ModalController,
    private router: Router,
  ) {}

  ngOnInit() {
    this.fetchPlanPurchase();
  }

  async fetchPlanPurchase(page: number = 1) {
    this.currentPage = page;
    this.isLoading = true;
    try {
      this.paginatedPurchases = await this.planPurchaseMasterService.readAll(page, {
        status: this.statusFilterValue(),
      });
    } finally {
      this.isLoading = false;
    }
  }

  onStatusSegmentChange() {
    this.fetchPlanPurchase();
  }

  async openPurchase(purchase: PlanPurchase) {
    const modal = await this.modalController.create({
      component: ViewPlanPurchaseDetailPage,
      componentProps: { purchase },
    });
    await modal.present();
    const { data } = await modal.onDidDismiss();
    if (data?.openOrganisationId) {
      await this.router.navigateByUrl(`menu/view-organisation/${data.openOrganisationId}`);
      return;
    }
    if (data?.refresh) {
      await this.fetchPlanPurchase(this.currentPage);
    }
  }

  private statusFilterValue(): PlanPurchaseStatusFilter | undefined {
    return this.statusSegment === 'all' ? undefined : this.statusSegment;
  }

  getStatus(purchase: PlanPurchase): PurchaseStatus {
    const expireTime = new Date(purchase.expireTime).getTime();
    if (expireTime > Date.now()) {
      return 'Active';
    }
    if (purchase.deactivateRemark?.trim()) {
      return 'Deactivated';
    }
    return 'Expired';
  }

  statusLabel(purchase: PlanPurchase): string {
    return this.getStatus(purchase);
  }

  statusClass(purchase: PlanPurchase): string {
    switch (this.getStatus(purchase)) {
      case 'Active':
        return 'bg-green-100 text-green-700';
      case 'Deactivated':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-500';
    }
  }
}
